import React, { useContext, useState, Suspense, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
import ImpersonationBanner from "../components/platform/ImpersonationBanner";
import { PlatformErrorBoundary } from "../components/platform/PlatformErrorBoundary";
import CommandPalette from "../components/platform/CommandPalette";
import PlatformHealthBar from "../components/platform/PlatformHealthBar";
import {
  LogOut,
  Clock,
  Menu,
  ChevronLeft,
  BarChart3,
  Globe,
  Settings,
  Terminal,
  Brain,
  CreditCard,
  ChevronRight,
  ShieldCheck,
  Activity,
  Sparkles,
  ScrollText,
  KeyRound,
  TrendingUp,
  Users,
  LayoutDashboard,
  FileBarChart,
} from "lucide-react";

const TITLE_MAP: Record<string, string> = {
  "/platform":             "Global Overview",
  "/platform/portfolio":   "Portfolio Health",
  "/platform/revenue":     "Revenue Analytics",
  "/platform/tenants":     "Tenant Management",
  "/platform/subscriptions": "Billing Control",
  "/platform/events":      "Event Monitor",
  "/platform/anomalies":   "Intelligence Center",
  "/platform/security":    "Security Intelligence",
  "/platform/ai-insights": "AI Insights",
  "/platform/reports":     "Reports Hub",
  "/platform/audit":       "Audit Trail",
  "/platform/permissions": "Permission Center",
  "/platform/settings":    "System Configuration",
};

const getTitle = (pathname: string): string => {
  // Exact match first
  if (TITLE_MAP[pathname]) return TITLE_MAP[pathname];
  // Prefix match for drilldown routes (e.g. /platform/tenants/42)
  const match = Object.keys(TITLE_MAP)
    .filter(k => k !== "/platform" && pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return match ? TITLE_MAP[match] : "Platform Console";
};

const PlatformLayout: React.FC = () => {
  const { user, platformUser, platformLogout, logout } = useContext(AuthContext);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    platformLogout();
    navigate("/platform/login");
  };

  const handleIdleLogout = useCallback(() => {
    platformLogout();
    navigate("/platform/login?expired=true");
  }, [platformLogout, navigate]);

  const { showWarning, secondsLeft, extendSession } = useIdleTimeout({
    timeoutMs: 15 * 60 * 1000,
    warningMs: 2 * 60 * 1000,
    onLogout: handleIdleLogout,
  });

  const exitImpersonation = () => {
    logout();
    navigate("/platform");
  };

  const pageTitle = getTitle(location.pathname);

  return (
    <div className="flex min-h-screen bg-background font-sans">
      <ImpersonationBanner user={user} onExit={exitImpersonation} />

      {/* ───────── Sidebar ───────── */}
      <aside className={`
        ${collapsed ? "w-20" : "w-72"}
        bg-surface border-r border-border transition-all duration-300
        flex flex-col relative z-50 shadow-sm sticky top-0 h-screen self-start overflow-y-hidden
        ${user?.impersonationMode ? "pt-16" : "pt-0"}
      `}>

        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-border bg-slate-50/30 shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-primary rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary/20">
                <Globe size={20} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-text uppercase tracking-tight">Platform</span>
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest -mt-1">Cockpit v2.0</span>
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 rounded-lg hover:bg-slate-100 text-muted transition-colors ml-auto"
          >
            {collapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-0.5 overflow-y-auto custom-scrollbar">

          {/* ── OVERVIEW ── */}
          <SectionLabel label="Overview" collapsed={collapsed} />
          <NavItem to="/platform"           icon={<BarChart3 size={18} />}       label="Global Cockpit"    collapsed={collapsed} end />
          <NavItem to="/platform/portfolio" icon={<TrendingUp size={18} />}      label="Portfolio Health"  collapsed={collapsed} />
          <NavItem to="/platform/revenue"   icon={<LayoutDashboard size={18} />} label="Revenue Analytics" collapsed={collapsed} />

          {/* ── OPERATIONS ── */}
          <SectionDivider collapsed={collapsed} label="Operations" />
          <NavItem to="/platform/tenants"       icon={<Users size={18} />}      label="Tenant Registry" collapsed={collapsed} />
          <NavItem to="/platform/subscriptions" icon={<CreditCard size={18} />} label="Subscriptions"   collapsed={collapsed} />
          <NavItem to="/platform/events"        icon={<Terminal size={18} />}   label="Event Monitor"   collapsed={collapsed} />

          {/* ── INTELLIGENCE ── */}
          <SectionDivider collapsed={collapsed} label="Intelligence" />
          <NavItem to="/platform/anomalies" icon={<Brain size={18} />}        label="Anomaly Center" collapsed={collapsed} />
          <NavItem to="/platform/security"  icon={<ShieldCheck size={18} />}  label="Security Intel" collapsed={collapsed} />
          <NavItem to="/platform/reports"   icon={<FileBarChart size={18} />} label="Reports Hub"    collapsed={collapsed} />

          {/* ── GOVERNANCE ── */}
          <SectionDivider collapsed={collapsed} label="Governance" />
          <NavItem to="/platform/audit"       icon={<ScrollText size={18} />} label="Audit Trail"      collapsed={collapsed} />
          <NavItem to="/platform/permissions" icon={<KeyRound size={18} />}   label="Permission Center" collapsed={collapsed} />

          {/* ── INFRASTRUCTURE ── */}
          <SectionDivider collapsed={collapsed} label="Infrastructure" />
          <NavItem to="/platform/settings" icon={<Settings size={18} />} label="System Config" collapsed={collapsed} />

          {/* ── LABS ── */}
          <SectionDivider collapsed={collapsed} label="Labs" />
          <NavItem to="/platform/ai-insights" icon={<Sparkles size={18} />} label="AI Insights" collapsed={collapsed} badge="beta" />

        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-slate-50/50 shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-muted hover:bg-danger/5 hover:text-danger transition-all group"
          >
            <LogOut size={18} className="group-hover:translate-x-1 transition-transform shrink-0" />
            {!collapsed && (
              <span className="font-bold text-[11px] uppercase tracking-widest">Terminate Session</span>
            )}
          </button>
        </div>
      </aside>

      {/* ───────── Main ───────── */}
      <main className={`flex-1 flex flex-col min-w-0 bg-background ${user?.impersonationMode ? "pt-16" : "pt-0"}`}>

        {/* Top Header */}
        <header className="h-20 px-10 flex items-center justify-between border-b border-border bg-surface/80 backdrop-blur-md sticky top-0 z-40 shrink-0">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-[0.2em] mb-0.5">
              <span>Platform</span>
              <ChevronRight size={10} />
              <span className="text-primary">Admin Console</span>
            </div>
            <h1 className="text-xl font-bold text-text tracking-tight">{pageTitle}</h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Health heartbeat */}
            <PlatformHealthBar />

            {/* ⌘K hint */}
            <button
              onClick={() => {
                const e = new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true });
                window.dispatchEvent(e);
              }}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-border
                         text-[11px] text-muted hover:bg-slate-50 transition-colors group"
              title="Open command palette (Ctrl+K)"
            >
              <span>Search commands</span>
              <kbd className="text-[9px] font-bold bg-slate-100 border border-border px-1.5 py-0.5 rounded group-hover:border-primary/30 transition-colors">
                ⌘K
              </kbd>
            </button>

            <div className="flex flex-col items-end mr-1 hidden sm:flex">
              <span className="text-sm font-bold text-text leading-none">
                {platformUser?.email?.split("@")[0]}
              </span>
              <span className="text-[9px] font-bold text-primary uppercase tracking-widest mt-1">
                Super Admin
              </span>
            </div>
            <div className="h-10 w-10 rounded-xl bg-softBlue flex items-center justify-center text-primary font-semibold border border-primary/10 shadow-sm">
              {platformUser?.email?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>

        {/* Viewport */}
        <div className="flex-1 p-10 custom-scrollbar">
          <div className="max-w-7xl mx-auto">
            <PlatformErrorBoundary>
              <Suspense fallback={<div className="flex items-center justify-center py-24"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>}>
                <Outlet />
              </Suspense>
            </PlatformErrorBoundary>
          </div>
        </div>
      </main>

      {/* ⌘K Command Palette — rendered outside sidebar/main so it overlays everything */}
      <CommandPalette />

      {/* ── Idle Session Warning Modal ── */}
      {showWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 flex flex-col items-center gap-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
              <Clock className="w-7 h-7 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Still there?</h3>
              <p className="text-sm text-gray-500 mt-1">
                Your platform session will expire in{" "}
                <span className="font-bold text-amber-600">
                  {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")}
                </span>
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={handleIdleLogout}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Sign out
              </button>
              <button
                onClick={extendSession}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── Sub-components ─────────────────────────── */

const SectionLabel = ({ label, collapsed }: { label: string; collapsed: boolean }) => {
  if (collapsed) return null;
  return (
    <p className="text-[9px] font-bold text-muted/40 uppercase tracking-[0.25em] px-4 pt-2 pb-1.5">
      {label}
    </p>
  );
};

const SectionDivider = ({ label, collapsed }: { label: string; collapsed: boolean }) => (
  <div className={`${collapsed ? "my-3" : "mt-5 mb-1"}`}>
    {collapsed
      ? <div className="h-px bg-border mx-2" />
      : <p className="text-[9px] font-bold text-muted/40 uppercase tracking-[0.25em] px-4 pt-2 pb-1.5">{label}</p>
    }
  </div>
);

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  end?: boolean;
  badge?: "soon" | "new" | "beta";
}

const BADGE_STYLES: Record<string, string> = {
  soon: "bg-slate-100 text-slate-400 border-slate-200",
  new:  "bg-emerald-50 text-emerald-600 border-emerald-200",
  beta: "bg-amber-50 text-amber-600 border-amber-200",
};

const NavItem = ({ to, icon, label, collapsed, end = false, badge }: NavItemProps) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `
      flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group
      ${isActive
        ? "bg-primary text-white shadow-lg shadow-primary/20 font-bold"
        : "text-muted hover:bg-slate-50 hover:text-text font-medium"}
    `}
  >
    <div className={`shrink-0 transition-transform ${collapsed ? "mx-auto" : ""}`}>{icon}</div>
    {!collapsed && (
      <>
        <span className="text-sm tracking-tight flex-1">{label}</span>
        {badge && (
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${BADGE_STYLES[badge]}`}>
            {badge}
          </span>
        )}
      </>
    )}
  </NavLink>
);

export default PlatformLayout;
